// pages/overlay/Alerts.jsx
// The always-on alerts overlay. Mounted as an OBS Browser Source layered on
// top of every other scene's source list. Fully transparent — only the toasts
// themselves are visible.
//
// Add `?debug` to the URL (e.g. /overlay/alerts?debug) to render a small HUD
// in the corner showing auth + listener state. Useful when an alert isn't
// firing and you need to see WHY without opening the dev console.
//
// Audio gating: browsers block audio playback until a user gesture happens.
// On OBS Browser Sources autoplay is enabled, so the audio context starts in
// "running" state and the prompt never shows. In a regular browser preview
// the context starts "suspended" and we show a click-to-enable card.
import { useEffect, useState } from "react";
import useOverlayPalette from "./../../hooks/useOverlayPalette";
import AlertQueue from "./../../components/overlay/AlertQueue";
import { getAudioState, resumeAudio } from "./../../utils/alertSounds";

export default function Alerts() {
  useOverlayPalette();
  const isDebug = new URLSearchParams(window.location.search).has("debug");
  const [debug, setDebug] = useState(null);
  const [needsAudio, setNeedsAudio] = useState(() => {
    const s = getAudioState();
    return s.supported && s.state !== "running";
  });

  // Re-check audio state on mount in case the context wasn't created yet
  useEffect(() => {
    const s = getAudioState();
    setNeedsAudio(s.supported && s.state !== "running");
  }, []);

  async function enableAudio() {
    try {
      await resumeAudio();
    } catch {}
    const s = getAudioState();
    setNeedsAudio(s.supported && s.state !== "running");
  }

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        background: isDebug ? "#0d1b2a" : "transparent",
        fontFamily: "system-ui, -apple-system, sans-serif",
        userSelect: "none",
        overflow: "hidden",
        position: "relative",
        // Pointer events on so the click-to-enable prompt can capture clicks.
        // The prompt itself is the only interactive element; it dismisses on
        // first click and after that the page is purely visual.
        pointerEvents: "auto",
      }}
    >
      <AlertQueue onDebug={setDebug} />

      {/* Click-to-enable-sound prompt — visible only when audio is suspended.
          Doesn't show on OBS Browser Sources (autoplay enabled there). */}
      {needsAudio && (
        <div
          onClick={enableAudio}
          style={{
            position: "absolute", inset: 0, zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(13, 27, 42, 0.85)",
            cursor: "pointer",
          }}
        >
          <div style={{
            background: "var(--jop-navy)",
            border: "3px solid var(--jop-gold)",
            borderRadius: "20px",
            padding: "2rem 2.5rem",
            textAlign: "center",
            maxWidth: "480px",
            boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
          }}>
            <div style={{ fontSize: "3.5rem", marginBottom: "0.5rem" }}>🔊</div>
            <div style={{
              color: "var(--jop-gold)",
              fontWeight: 900,
              fontSize: "1.6rem",
              letterSpacing: "0.02em",
              marginBottom: "0.5rem",
            }}>Click anywhere to enable alert sounds</div>
            <div style={{
              color: "rgba(255,255,255,0.65)",
              fontSize: "0.9rem",
              lineHeight: 1.5,
            }}>
              Browsers block audio until you interact with the page.<br />
              <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem" }}>
                (You won't see this prompt in OBS — audio just works there.)
              </span>
            </div>
          </div>
        </div>
      )}

      {isDebug && (
        <div style={{
          position: "absolute", top: "1rem", left: "1rem",
          background: "rgba(0,0,0,0.85)",
          color: "white",
          padding: "0.85rem 1.1rem",
          borderRadius: "10px",
          border: "1px solid rgba(255,255,255,0.2)",
          fontSize: "0.85rem",
          fontFamily: "monospace",
          lineHeight: 1.6,
          minWidth: "260px",
          pointerEvents: "auto",
        }}>
          <div style={{ color: "var(--jop-gold)", fontWeight: "bold", marginBottom: "0.4rem", letterSpacing: "0.05em" }}>
            🔔 ALERTS DEBUG
          </div>
          <DebugRow label="Authed"   value={debug?.authed ? "✅ yes" : "❌ no"} />
          <DebugRow label="Listening" value={debug?.listening ? "✅ yes" : "❌ no"} />
          <DebugRow label="Total docs" value={debug?.totalDocs ?? "—"} />
          <DebugRow label="Live (unexpired)" value={debug?.pendingCount ?? "—"} />
          <DebugRow label="Audio" value={needsAudio ? "🔇 suspended" : "🔊 running"} color={needsAudio ? "var(--jop-gold)" : "#70be44"} />
          <DebugRow label="Last error" value={debug?.lastError || "none"} color={debug?.lastError ? "var(--jop-pink)" : "#70be44"} />
          <div style={{
            color: "rgba(255,255,255,0.5)", fontSize: "0.7rem", marginTop: "0.6rem",
            paddingTop: "0.4rem", borderTop: "1px solid rgba(255,255,255,0.15)",
          }}>
            Open console (⌥⌘I) for full logs.<br />
            Drop ?debug from URL for transparent overlay.
          </div>
        </div>
      )}
    </div>
  );
}

function DebugRow({ label, value, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}>
      <span style={{ color: "rgba(255,255,255,0.55)" }}>{label}</span>
      <span style={{ color: color || "white" }}>{String(value)}</span>
    </div>
  );
}
